import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const SCREENSHOT_BUCKET = "bug-screenshots";
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5MB

// ============= Schemas =============

const consoleEntrySchema = z.object({
  level: z.enum(["log", "warn", "error", "info"]),
  message: z.string().max(500),
  ts: z.string().max(40),
});

const contextSchema = z.object({
  url: z.string().max(2000).optional(),
  routePattern: z.string().max(500).optional(),
  userAgent: z.string().max(500).optional(),
  viewport: z.string().max(40).optional(),
  appVersion: z.string().max(80).optional(),
  consoleBuffer: z.array(consoleEntrySchema).max(20).optional(),
});

const submitManualSchema = contextSchema.extend({
  description: z.string().trim().min(10).max(2000),
  screenshot: z
    .object({
      base64: z.string().min(1),
      mimeType: z
        .string()
        .regex(/^image\/(png|jpeg|jpg|webp|gif)$/i, "Unsupported image type"),
      filename: z.string().max(200).optional(),
    })
    .nullable()
    .optional(),
});

const submitErrorSchema = contextSchema.extend({
  errorMessage: z.string().max(2000).optional(),
  errorStack: z.string().max(20000).optional(),
});

// ============= Submit (manual via dialog) =============
export const submitBugReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => submitManualSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId, supabase } = context;

    // Snapshot email
    const { data: claims } = await supabase.auth.getUser();
    const email = claims.user?.email ?? null;

    // Insert row first to get an ID for the screenshot path
    const { data: row, error: insErr } = await supabaseAdmin
      .from("bug_reports")
      .insert({
        user_id: userId,
        user_email_snapshot: email,
        description: data.description,
        url: data.url ?? null,
        route_pattern: data.routePattern ?? null,
        user_agent: data.userAgent ?? null,
        viewport: data.viewport ?? null,
        app_version: data.appVersion ?? null,
        console_buffer: data.consoleBuffer ?? [],
        source: "manual",
      })
      .select("id")
      .single();
    if (insErr || !row) throw new Error(insErr?.message ?? "Insert failed");

    // Optional screenshot upload
    if (data.screenshot) {
      const bytes = Uint8Array.from(atob(data.screenshot.base64), (c) =>
        c.charCodeAt(0),
      );
      if (bytes.length > MAX_SCREENSHOT_BYTES) {
        // Roll back the row to keep things clean
        await supabaseAdmin.from("bug_reports").delete().eq("id", row.id);
        throw new Error("Screenshot too large (max 5MB)");
      }
      const ext = data.screenshot.mimeType.split("/")[1]?.replace("jpeg", "jpg") ?? "png";
      const path = `${userId}/${row.id}.${ext}`;
      const { error: upErr } = await supabaseAdmin.storage
        .from(SCREENSHOT_BUCKET)
        .upload(path, bytes, {
          contentType: data.screenshot.mimeType,
          upsert: false,
        });
      if (upErr) {
        await supabaseAdmin.from("bug_reports").delete().eq("id", row.id);
        throw new Error(`Screenshot upload failed: ${upErr.message}`);
      }
      await supabaseAdmin
        .from("bug_reports")
        .update({ screenshot_path: path })
        .eq("id", row.id);
    }

    return { id: row.id };
  });

// ============= Submit (one-click from error boundary) =============
export const submitErrorReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => submitErrorSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId, supabase } = context;
    const { data: claims } = await supabase.auth.getUser();
    const email = claims.user?.email ?? null;

    const { data: row, error } = await supabaseAdmin
      .from("bug_reports")
      .insert({
        user_id: userId,
        user_email_snapshot: email,
        description: null,
        url: data.url ?? null,
        route_pattern: data.routePattern ?? null,
        user_agent: data.userAgent ?? null,
        viewport: data.viewport ?? null,
        app_version: data.appVersion ?? null,
        console_buffer: data.consoleBuffer ?? [],
        error_message: data.errorMessage ?? null,
        error_stack: data.errorStack ?? null,
        source: "error_boundary",
      })
      .select("id")
      .single();
    if (error || !row) throw new Error(error?.message ?? "Insert failed");
    return { id: row.id };
  });
